import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import { Plus, Target, TrendingUp, ChevronRight } from "lucide-react";
import { Fundraise, FundraisingAnalytics } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface RaiseWithAnalytics extends Fundraise {
  analytics?: FundraisingAnalytics;
}

export default function RaiseList() {
  const navigate = useNavigate();
  const [raises, setRaises] = useState<RaiseWithAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadRaises();
  }, []);

  const loadRaises = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const transformed: RaiseWithAnalytics[] = await Promise.all(
          data.map(async (f: any) => {
            // Load analytics for each raise
            let analytics: FundraisingAnalytics | undefined;
            try {
              const analyticsResponse = await fetch(`${API_URL}/fundraising/raises/${f.id}/analytics`, {
                headers: { 'x-user-id': userId, 'x-user-role': userRole }
              });
              if (analyticsResponse.ok) {
                analytics = await analyticsResponse.json();
              }
            } catch (e) {
              console.error('Failed to load analytics for raise:', f.id);
            }

            return {
              id: f.id,
              name: f.name,
              description: f.description,
              status: f.status,
              raiseGoal: f.raise_goal,
              linkedVdrId: f.linked_vdr_id,
              linkedVdr: f.vdr_rooms,
              createdAt: f.created_at,
              updatedAt: f.updated_at,
              analytics
            };
          })
        );
        // Sort active first, then by date
        transformed.sort((a, b) => {
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (a.status !== 'active' && b.status === 'active') return 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setRaises(transformed);
      }
    } catch (e) {
      console.error('Failed to load raises:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'paused': return 'bg-amber-100 text-amber-700';
      case 'closed': return 'bg-neutral-100 text-neutral-500';
      default: return 'bg-neutral-100 text-neutral-500';
    }
  };

  // Calculate totals across all raises
  const totals = raises.reduce((acc, raise) => {
    if (raise.analytics) {
      acc.totalFunds += raise.analytics.totalFunds;
      acc.activeFunds += raise.analytics.activeFunds;
      acc.totalTermSheets += raise.analytics.totalTermSheets;
      acc.committedCapital += raise.analytics.committedCapital;
      acc.pendingCapital += raise.analytics.pendingCapital;
      acc.raiseGoal += raise.analytics.raiseGoal;
    }
    return acc;
  }, {
    totalFunds: 0,
    activeFunds: 0,
    totalTermSheets: 0,
    committedCapital: 0,
    pendingCapital: 0,
    raiseGoal: 0
  });

  const activeRaises = raises.filter(r => r.status === 'active');
  const closedRaises = raises.filter(r => r.status === 'closed');

  const columns = useMemo(() => [
    {
        id: "name",
        header: "Raise",
        accessorFn: (row: any) => row.name,
        cell: ({ row }: any) => (
            <Link to={`/fundraising/raises/${row.original.id}`} className="group block py-1">
                <div className="flex items-center gap-2 mb-0.5">
                     <span className="font-medium text-neutral-900 group-hover:underline decoration-neutral-400 decoration-dotted underline-offset-2">{row.original.name}</span>
                     <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(row.original.status)}`}>
                         {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
                     </span>
                </div>
                {row.original.description && (
                    <div className="text-sm text-neutral-500 truncate max-w-xs">{row.original.description}</div>
                )}
            </Link>
        )
    },
    {
        id: "progress",
        header: "Progress",
        cell: ({ row }: any) => {
            const raise = row.original;
            if (!raise.analytics || !raise.raiseGoal) return <span className="text-neutral-400">-</span>;
            return (
                <div className="w-48">
                     <div className="flex justify-between text-xs text-neutral-500 mb-1">
                         <span>{formatCurrency(raise.analytics.committedCapital)}</span>
                         <span>Goal: {formatCurrency(raise.raiseGoal)}</span>
                     </div>
                     <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                         <div 
                            className={`h-full rounded-full ${raise.analytics.oversubscriptionPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(raise.analytics.oversubscriptionPct, 100)}%` }}
                         />
                     </div>
                </div>
            );
        }
    },
    {
        id: "stats",
        header: "Stats",
        cell: ({ row }: any) => {
            const raise = row.original;
            if (!raise.analytics) return <span className="text-neutral-400">-</span>;
            return (
                <div className="flex items-center gap-4 text-sm text-neutral-600">
                     <div className="flex items-center gap-1.5" title="Funds">
                         <Target className="w-3 h-3 text-neutral-400" />
                         <span>{raise.analytics.totalFunds} funds</span>
                     </div>
                     <div className="flex items-center gap-1.5" title="Term Sheets">
                         <TrendingUp className="w-3 h-3 text-neutral-400" />
                         <span>{raise.analytics.totalTermSheets} sheets</span>
                     </div>
                </div>
            );
        }
    },
    {
        id: "created",
        header: "Created",
        accessorFn: (row: any) => row.createdAt,
        cell: ({ row }: any) => (
            <div className="flex items-center gap-1.5 text-neutral-500 text-sm">
                <span>{formatDate(row.original.createdAt)}</span>
            </div>
        )
    },
    {
        id: "actions",
        header: "",
        size: 50,
        cell: () => (
            <div className="flex justify-end">
                <ChevronRight className="w-4 h-4 text-neutral-400" />
            </div>
        )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: raises,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: raises.length,
    resourceName: (plural) => `raise${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Your Raises</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Your Raises</h1>
          <p className="text-neutral-500">Manage your fundraising campaigns</p>
        </div>
        {userRole === 'super_admin' && (
          <Button 
            onClick={() => navigate('/fundraising/raises/new/settings')}
            icon={<Plus className="w-4 h-4" />}
            text="New Raise"
          />
        )}
      </div>

      {/* Summary Stats */}
      {raises.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Active Raises"
            value={activeRaises.length.toString()}
            hint={`${closedRaises.length} closed`}
          />
          <StatCard
            label="Total Funds"
            value={totals.totalFunds.toString()}
            hint={`${totals.activeFunds} active`}
          />
          <StatCard
            label="Committed Capital"
            value={formatCurrency(totals.committedCapital)}
            hint={`${totals.totalTermSheets} term sheets`}
          />
          <StatCard
            label="Pipeline Value"
            value={formatCurrency(totals.pendingCapital)}
            hint="From pending term sheets"
          />
        </div>
      )}

      {/* Raises Table */}
      {raises.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Raises Yet</h3>
            <p className="text-neutral-500 mb-6">Create your first fundraise to get started.</p>
            {userRole === 'super_admin' && (
              <Button 
                onClick={() => navigate('/fundraising/raises/new/settings')}
                text="Create Fundraise"
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
             <Table {...tableProps} table={table} />
        </Card>
      )}
    </div>
  );
}
